/**
 * @license
 * © 2026 NexoraX Digital Ltd. All Rights Reserved.
 * US Patent Pending. NexoraX NexoraX Communications Node Framework.
 * Licensed under the Apache License, Version 2.0 (the "License").
 */

import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from "recharts";
import { 
  Mic, 
  Activity, 
  TrendingUp, 
  CheckCircle, 
  ShieldAlert, 
  Sliders, 
  Trash2, 
  RefreshCw, 
  Database,
  Plus,
  Play
} from "lucide-react";

// Types matching the RecognizedHistoryItem structure in VoiceCommandManager
interface RecognizedHistoryItem {
  timestamp: string; // "toLocaleTimeString() toLocaleDateString()"
  intentSpoken: string;
  matchedAction: string;
  confidence: number;
  category: string;
  status: "GRANTED" | "BLOCKED" | "UNRESOLVED";
}

// Pre-populated realistic history entries for a fully live, rich environment visualization
const DEFAULT_HISTORICAL_DATA: RecognizedHistoryItem[] = [
  {
    timestamp: "10:30:15 08/06/2026",
    intentSpoken: "authenticate director",
    matchedAction: "MFA Authentication Handshake Bypass",
    confidence: 0.98,
    category: "Security",
    status: "GRANTED"
  },
  {
    timestamp: "10:45:22 08/06/2026",
    intentSpoken: "lock administrative terminal",
    matchedAction: "LOCK_TERMINAL",
    confidence: 0.89,
    category: "System Admin",
    status: "GRANTED"
  },
  {
    timestamp: "11:02:10 08/06/2026",
    intentSpoken: "simulate revenue core flow",
    matchedAction: "SIMULATE_REVENUE_INFLOW",
    confidence: 0.92,
    category: "NexoraX Bank",
    status: "GRANTED"
  },
  {
    timestamp: "11:15:45 08/06/2026",
    intentSpoken: "override database replication index",
    matchedAction: "Database Wipe Probe",
    confidence: 0.54,
    category: "Security",
    status: "BLOCKED"
  },
  {
    timestamp: "11:32:01 08/06/2026",
    intentSpoken: "trigger security alarm protocol",
    matchedAction: "TRIGGER_GAVEL_ALARM",
    confidence: 0.95,
    category: "Security",
    status: "GRANTED"
  },
  {
    timestamp: "12:05:12 08/06/2026",
    intentSpoken: "open tab NexoraX Resource Library",
    matchedAction: "OPEN_TAB: NexoraX Resource Library",
    confidence: 0.81,
    category: "Navigation",
    status: "GRANTED"
  },
  {
    timestamp: "12:55:40 08/06/2026",
    intentSpoken: "calculate NexoraX 10% Maintenance & Hosting Fee",
    matchedAction: "CALCULATE_NEXORAX_TAX",
    confidence: 0.91,
    category: "NexoraX Bank",
    status: "GRANTED"
  },
  {
    timestamp: "13:10:04 08/06/2026",
    intentSpoken: "deploy viral Client Resource Workflow swarm",
    matchedAction: "NexoraX Talent Swarm Launch",
    confidence: 0.76,
    category: "Secure Invoice Register Operations",
    status: "GRANTED"
  },
  {
    timestamp: "13:42:19 08/06/2026",
    intentSpoken: "toggle active noise filtering",
    matchedAction: "TOGGLE_ANC_BOOST",
    confidence: 0.65,
    category: "System Admin",
    status: "GRANTED"
  },
  {
    timestamp: "14:15:33 08/06/2026",
    intentSpoken: "extract non-existent external server log",
    matchedAction: "Log Wipe Attempt",
    confidence: 0.38,
    category: "Security",
    status: "BLOCKED"
  }
];

// Helper to convert historical items to timeline data points
const generateTrendlineData = (history: RecognizedHistoryItem[]) => {
  // Sort by timestamp if needed, but since we want to present a trendline,
  // let's build standard success rate indices mapped over time buckets.
  // For standard user display, we extract the hour or represent the last 10 entries visually.
  return [...history].reverse().map((item, idx) => {
    const timeMatch = item.timestamp.split(" ")[0] || `T+${idx}`;
    return {
      name: timeMatch,
      confidence: Math.round(item.confidence * 100),
      successRate: item.status === "GRANTED" ? 100 : item.status === "BLOCKED" ? 0 : 50,
      cumulativeAccuracy: Math.round(
        (history.slice(history.length - 1 - idx).filter(h => h.status === "GRANTED").length / 
        (idx + 1)) * 100
      )
    };
  });
};

export const VoiceMetricsDashboard: React.FC = () => {
  const [history, setHistory] = useState<RecognizedHistoryItem[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_vcm_history");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.length > 0) return parsed;
        } catch (e) {
          console.error("Failed to parse standard voice history logs:", e);
        }
      }
    }
    return DEFAULT_HISTORICAL_DATA;
  });

  const [timeFilter, setTimeFilter] = useState<"ALL" | "SECURITY" | "BANK" | "ADMIN">("ALL");
  const [simulationPrompt, setSimulationPrompt] = useState("");
  const [simulationCategory, setSimulationCategory] = useState("NexoraX Bank");
  const [simulationStatus, setSimulationStatus] = useState<"GRANTED" | "BLOCKED" | "UNRESOLVED">("GRANTED");
  const [simulationConfidence, setSimulationConfidence] = useState(0.95);

  // Sync back to local storage to ensure other nodes can verify metric updates
  const saveHistory = (updated: RecognizedHistoryItem[]) => {
    setHistory(updated);
    if (typeof window !== "undefined") {
      localStorage.setItem("nexorax_vcm_history", JSON.stringify(updated));
    }
  };

  const handleClearHistory = () => {
    if (window.confirm("Confirm purge of NexoraX Voice Command metrics ledger? All historical data points will be reset.")) {
      saveHistory([]);
    }
  };

  const handleResetDefaults = () => {
    saveHistory(DEFAULT_HISTORICAL_DATA);
  };

  // Live simulation system so users can create, simulate, and observe live updates on Recharts lines immediately
  const handleTriggerSimulation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!simulationPrompt.trim()) return;

    const matchedActionMap: Record<string, string> = {
      "simulate revenue flow": "SIMULATE_REVENUE_INFLOW",
      "calculate tax": "CALCULATE_NEXORAX_TAX",
      "trigger alarm": "TRIGGER_GAVEL_ALARM",
      "toggle noise boost": "TOGGLE_ANC_BOOST",
      "open terminal": "OPEN_TAB: NODE 0: ARCHITECT TERMINAL",
      "lock panel": "LOCK_TERMINAL"
    };

    const actionText = matchedActionMap[simulationPrompt.toLowerCase().trim()] || "Custom Simulated Operator Intent";

    const newRecord: RecognizedHistoryItem = {
      timestamp: new Date().toLocaleTimeString() + " " + new Date().toLocaleDateString(),
      intentSpoken: simulationPrompt.trim().toLowerCase(),
      matchedAction: actionText,
      confidence: simulationConfidence,
      category: simulationCategory,
      status: simulationStatus
    };

    saveHistory([newRecord, ...history]);
    setSimulationPrompt("");

    // Dispatch global system event so that the voice command log visualizer and text feedbacks reload
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("nexorax-vcm-history-updated", { detail: newRecord }));
    }
  };

  // Filtering calculations
  const filteredHistory = history.filter(item => {
    if (timeFilter === "ALL") return true;
    if (timeFilter === "SECURITY") return item.category.toLowerCase().includes("security");
    if (timeFilter === "BANK") return item.category.toLowerCase().includes("bank");
    if (timeFilter === "ADMIN") return item.category.toLowerCase().includes("admin") || item.category.toLowerCase().includes("system");
    return true;
  });

  // KPI metric aggregations
  const totalCommands = filteredHistory.length;
  const grantedCount = filteredHistory.filter(h => h.status === "GRANTED").length;
  const blockedCount = filteredHistory.filter(h => h.status === "BLOCKED").length;
  const unresolvedCount = filteredHistory.filter(h => h.status === "UNRESOLVED").length;
  const successRate = totalCommands > 0 ? Math.round((grantedCount / totalCommands) * 100) : 100;
  
  const avgConfidence = totalCommands > 0 
    ? Math.round((filteredHistory.reduce((acc, curr) => acc + curr.confidence, 0) / totalCommands) * 100) 
    : 95;

  // Most frequently used voice command counts calculation
  const commandFrequencyMap: Record<string, { count: number; category: string; successCount: number }> = {};
  filteredHistory.forEach(item => {
    const key = item.intentSpoken.toLowerCase().trim();
    if (!commandFrequencyMap[key]) {
      commandFrequencyMap[key] = { count: 0, category: item.category, successCount: 0 };
    }
    commandFrequencyMap[key].count += 1;
    if (item.status === "GRANTED") {
      commandFrequencyMap[key].successCount += 1;
    }
  });

  const commandFrequencyData = Object.entries(commandFrequencyMap).map(([command, info]) => ({
    command: `"${command}"`,
    count: info.count,
    category: info.category,
    successRate: Math.round((info.successCount / info.count) * 100)
  })).sort((a, b) => b.count - a.count).slice(0, 5);

  // Success Rates over time timeline data point generation
  const timelineData = generateTrendlineData(filteredHistory);

  return (
    <div id="voice-metrics-section-container" className="space-y-6 w-full text-zinc-100">
      
      {/* Header Info Band */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-xl border border-indigo-500/10 bg-gradient-to-r from-slate-900/40 via-indigo-950/10 to-zinc-950">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#e240f3]/10 border border-[#e240f3]/20 rounded-lg text-[#e240f3] animate-pulse shadow-[0_0_15px_rgba(226,64,243,0.15)]">
            <Mic className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[10px] font-mono font-black tracking-[0.2em] text-[#e240f3] uppercase block">SYSTEM MONITORING NODE // NODE 17</span>
            <h3 className="text-lg font-sans font-black text-white uppercase tracking-tight">Nexorax Voice Command Metrics</h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded px-2 py-1 gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-[9px] font-mono font-bold text-zinc-400 uppercase tracking-widest">STREAMING OK</span>
          </div>
          <button 
            type="button" 
            onClick={handleResetDefaults}
            className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 hover:border-zinc-700 text-[10px] font-mono font-medium rounded uppercase transition-colors flex items-center gap-1 cursor-pointer"
          >
            <RefreshCw className="w-3 h-3" /> Restore Base Metrics
          </button>
          <button 
            type="button" 
            onClick={handleClearHistory}
            className="px-3 py-1.5 bg-rose-950/20 hover:bg-rose-950/70 text-rose-300 border border-rose-900/30 hover:border-rose-500/40 text-[10px] font-mono font-medium rounded uppercase transition-colors flex items-center gap-1 cursor-pointer"
          >
            <Trash2 className="w-3 h-3" /> Purge Ledger
          </button>
        </div>
      </div>

      {/* Primary KPI Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { 
            label: "Total Voice Prompts", 
            value: totalCommands, 
            icon: <Mic className="w-4 h-4 text-[#e240f3]" />, 
            subtitle: "Total registered intent sequences",
            border: "border-indigo-500/10"
          },
          { 
            label: "Verification Accuracy", 
            value: `${successRate}%`, 
            icon: <CheckCircle className="w-4 h-4 text-emerald-400" />, 
            subtitle: "NexoraX validation success rate",
            border: "border-emerald-500/15"
          },
          { 
            label: "Mean Match Confidence", 
            value: `${avgConfidence}%`, 
            icon: <TrendingUp className="w-4 h-4 text-cyan-400" />, 
            subtitle: "Neural core confidence factor",
            border: "border-cyan-500/15"
          },
          { 
            label: "Mitigated Probes", 
            value: blockedCount, 
            icon: <ShieldAlert className="w-4 h-4 text-rose-400" />, 
            subtitle: "Secured unauthorized command bypasses",
            border: "border-rose-500/15"
          }
        ].map((kpi, idx) => (
          <div key={idx} className={`p-4 bg-black/40 border ${kpi.border} rounded-lg shadow-sm flex flex-col justify-between`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-mono font-bold text-zinc-500 uppercase tracking-widest">{kpi.label}</span>
              {kpi.icon}
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-mono font-bold text-white tracking-tight">{kpi.value}</div>
              <span className="text-[8px] text-zinc-500 font-mono uppercase block mt-1">{kpi.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Recharts Success over Time Line Chart */}
        <div className="lg:col-span-8 bg-zinc-950/80 border border-zinc-900 rounded-xl p-5 flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#e240f3]"></span>
                <span className="text-[9px] font-mono font-bold text-zinc-400 uppercase tracking-wider">COMMAND PROGRESS PROFILE</span>
              </div>
              <h4 className="text-sm font-sans font-bold text-white uppercase tracking-normal">Success Rates &amp; Cumulative Accuracy Profile</h4>
            </div>
            
            {/* Classification Filters */}
            <div className="flex items-center bg-zinc-950 border border-zinc-900 rounded p-1 gap-1">
              {[
                { id: "ALL", label: "ALL" },
                { id: "SECURITY", label: "SEC" },
                { id: "BANK", label: "BANK" },
                { id: "ADMIN", label: "SYS" }
              ].map(filter => (
                <button 
                  key={filter.id}
                  type="button"
                  onClick={() => setTimeFilter(filter.id as any)}
                  className={`px-2.5 py-1 text-[8.5px] font-mono rounded tracking-tight uppercase transition-all font-bold cursor-pointer ${
                    timeFilter === filter.id 
                      ? "bg-[#e240f3] text-black" 
                      : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="h-[280px] w-full pt-2">
            {timelineData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-zinc-650 font-mono text-[9px] uppercase border border-dashed border-zinc-900 rounded">
                No telemetry segments captured. Run custom simulations below.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timelineData} margin={{ top: 8, right: 15, left: -20, bottom: 0 }}>
                  <XAxis 
                    dataKey="name" 
                    stroke="#52525b" 
                    fontSize={8} 
                    fontFamily="JetBrains Mono, monospace" 
                    tickLine={false}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    stroke="#52525b" 
                    fontSize={8} 
                    fontFamily="JetBrains Mono, monospace" 
                    tickLine={false}
                    axisLine={false}
                  />
                  <CartesianGrid stroke="#18181b" strokeDasharray="3 3" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "#09090b", 
                      borderColor: "#27272a", 
                      fontSize: "10px", 
                      fontFamily: "JetBrains Mono, monospace" ,
                      borderRadius: "6px"
                    }} 
                    itemStyle={{ color: "#e240f3" }}
                  />
                  <Legend 
                    wrapperStyle={{ 
                      fontSize: "9px", 
                      fontFamily: "JetBrains Mono, monospace",
                      marginTop: "10px"
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativeAccuracy" 
                    name="Cumulative Success Ratio" 
                    stroke="#10b981" 
                    strokeWidth={2.5} 
                    dot={{ fill: "#10b981", r: 4 }} 
                    activeDot={{ r: 6 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="confidence" 
                    name="Match Confidence (%)" 
                    stroke="#0ea5e9" 
                    strokeWidth={1.5} 
                    strokeDasharray="4 4"
                    dot={{ fill: "#0ea5e9", r: 2 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <span className="text-[8px] text-zinc-500 font-mono uppercase text-center block mt-1">
            X-Axis plots sequence instances timeline (new entries flow right to left)
          </span>
        </div>

        {/* Recharts Command Usage Frequency Bar Chart */}
        <div className="lg:col-span-4 bg-zinc-950/80 border border-zinc-900 rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500"></span>
              <span className="text-[9px] font-mono font-bold text-zinc-400 uppercase tracking-wider">COMMAND HIT FREQUENCIES</span>
            </div>
            <h4 className="text-sm font-sans font-bold text-white uppercase tracking-normal">Most Frequent Voice Intents</h4>
          </div>

          <div className="h-[210px] w-full pt-4">
            {commandFrequencyData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-zinc-650 font-mono text-[9px] uppercase border border-dashed border-zinc-900 rounded">
                Telemetry empty.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={commandFrequencyData} layout="vertical" margin={{ top: 0, right: 10, left: -25, bottom: 0 }}>
                  <XAxis type="number" stroke="#52525b" fontSize={8} fontFamily="JetBrains Mono, monospace" tickLine={false} />
                  <YAxis type="category" dataKey="command" stroke="#52525b" fontSize={8.5} fontFamily="JetBrains Mono, monospace" tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "#09090b", 
                      borderColor: "#27272a", 
                      fontSize: "9px", 
                      fontFamily: "JetBrains Mono, monospace" 
                    }} 
                  />
                  <Bar dataKey="count" fill="#e240f3" radius={[0, 4, 4, 0]}>
                    {commandFrequencyData.map((entry, index) => {
                      const colors = ["#e240f3", "#a855f7", "#3b82f6", "#06b6d4", "#10b981"];
                      const color = colors[index % colors.length];
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="border-t border-zinc-900/60 pt-3 mt-4 space-y-1.5">
            <span className="text-[8px] text-indigo-400 font-mono uppercase font-black block">Quick Statistics Breakdown:</span>
            <div className="grid grid-cols-2 gap-2 text-[8px] font-mono uppercase text-zinc-400">
              <div className="bg-zinc-950 p-1.5 rounded border border-zinc-900">
                <span className="text-zinc-600 block">Total Volume</span>
                <span className="font-bold text-white">{totalCommands} requests</span>
              </div>
              <div className="bg-zinc-950 p-1.5 rounded border border-zinc-900">
                <span className="text-zinc-600 block">Peak Trigger</span>
                <span className="font-bold text-zinc-200 truncate">{commandFrequencyData[0]?.command || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Simulator, Filter & Historical Ledger Section */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        
        {/* Live Simulation Operator Panel */}
        <div className="md:col-span-5 bg-zinc-950/80 border border-zinc-900 rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Sliders className="w-4 h-4 text-[#e240f3]" />
              <span className="text-[9px] font-mono font-bold text-zinc-400 uppercase tracking-wider">MANDATORY TESTING CRADLE</span>
            </div>
            <h4 className="text-sm font-sans font-bold text-white uppercase tracking-normal">Telemetry Pattern Simulator</h4>
            <p className="text-[9px] font-mono text-zinc-500 uppercase mt-0.5 leading-relaxed">
              Manually generate diagnostic vocal intent parameters here to evaluate graph reactions, success indices, and neural confidence tracking.
            </p>
          </div>

          <form onSubmit={handleTriggerSimulation} className="space-y-3.5 mt-4">
            
            <div className="space-y-1">
              <label className="text-[8.5px] font-mono font-bold text-zinc-400 uppercase tracking-widest block">Simulation Command Preset or Custom String</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  required
                  placeholder="e.g. simulate revenue flow or calculate tax"
                  value={simulationPrompt}
                  onChange={(e) => setSimulationPrompt(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 focus:border-[#e240f3] rounded px-3 py-1.5 text-xs font-mono text-zinc-200 outline-none flex-1 placeholder-zinc-800"
                />
                
                {/* Presets Quick Drawer */}
                <select 
                  value={simulationPrompt}
                  onChange={(e) => setSimulationPrompt(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 hover:border-zinc-700 rounded px-2 text-[10px] font-mono text-indigo-400 outline-none cursor-pointer"
                >
                  <option value="">[Presets]</option>
                  <option value="simulate revenue flow">Simulate Revenue Inflow</option>
                  <option value="calculate tax">Audit 10% Maintenance & Hosting Fee</option>
                  <option value="trigger alarm">Trigger Security Alarm</option>
                  <option value="toggle noise boost">Anc Boost State</option>
                  <option value="open terminal">Navigate Terminal</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[8.5px] font-mono font-bold text-zinc-400 uppercase tracking-widest block">System Domain Category</label>
                <select 
                  value={simulationCategory}
                  onChange={(e) => setSimulationCategory(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-[11px] font-mono text-zinc-200 outline-none cursor-pointer"
                >
                  <option value="NexoraX Bank">NexoraX Bank</option>
                  <option value="Security">Security</option>
                  <option value="Secure Invoice Register Operations">Secure Invoice Register Operations</option>
                  <option value="System Admin">System Admin</option>
                  <option value="Navigation">Navigation</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[8.5px] font-mono font-bold text-zinc-400 uppercase tracking-widest block">Simulation Verification</label>
                <select 
                  value={simulationStatus}
                  onChange={(e) => setSimulationStatus(e.target.value as any)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-[11px] font-mono text-zinc-200 outline-none cursor-pointer"
                >
                  <option value="GRANTED">GRANTED (Secured Validation)</option>
                  <option value="BLOCKED">BLOCKED (Probe Intercepted)</option>
                  <option value="UNRESOLVED">UNRESOLVED (Bypass Retrying)</option>
                </select>
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <div className="flex justify-between text-[8px] font-mono text-zinc-500 uppercase">
                <span>Verification Confidence Ratio:</span>
                <span className="text-[#e240f3] font-bold">{Math.round(simulationConfidence * 100)}%</span>
              </div>
              <input 
                type="range" 
                min="0.30" 
                max="1.00" 
                step="0.05" 
                value={simulationConfidence}
                onChange={(e) => setSimulationConfidence(parseFloat(e.target.value))}
                className="w-full accent-[#e240f3] cursor-pointer"
              />
            </div>

            <button
              type="submit"
              className="w-full py-2 mt-2 bg-[#e240f3]/20 hover:bg-[#e240f3] border border-[#e240f3]/35 hover:border-[#e240f3] text-[#e240f3] hover:text-black font-mono text-[10px] uppercase font-black tracking-widest rounded transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Inject Voice Telemetry Ingress
            </button>

          </form>
        </div>

        {/* Filtered Interactive Voice Command History Ledger */}
        <div className="md:col-span-7 bg-zinc-950/80 border border-zinc-900 rounded-xl p-5 flex flex-col h-[340px]">
          <div className="flex items-center justify-between border-b border-zinc-900/60 pb-2 mb-3">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-400" />
              <div>
                <h4 className="text-sm font-sans font-bold text-white uppercase tracking-normal">NexoraX Validation Logs</h4>
                <p className="text-[8px] font-mono text-zinc-500 uppercase">TELEMETRY GRID RECORD AUDIT JOURNAL</p>
              </div>
            </div>
            <span className="text-[10px] font-mono font-bold bg-[#e240f3]/5 px-2 py-0.5 rounded border border-[#e240f3]/10 text-[#e240f3]">
              {filteredHistory.length} logs
            </span>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 custom-scrollbar">
            {filteredHistory.length === 0 ? (
              <div className="h-full flex items-center justify-center text-zinc-600 font-mono text-[9px] uppercase border border-dashed border-zinc-900 rounded">
                No ledger logs found matching active time boundaries.
              </div>
            ) : (
              filteredHistory.map((item, idx) => (
                <div 
                  key={idx}
                  className="p-2.5 rounded bg-zinc-950 border border-zinc-900/80 hover:border-zinc-800 flex items-center justify-between text-left gap-4 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[10.5px] text-zinc-100 font-bold tracking-tight">
                        "{item.intentSpoken}"
                      </span>
                      <span className="text-[8px] bg-zinc-900 border border-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded font-mono uppercase">
                        {item.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[8.5px] font-mono text-zinc-500">
                      <span className="truncate max-w-[170px]" title={item.matchedAction}>
                        Action: <span className="text-zinc-400">{item.matchedAction}</span>
                      </span>
                      <span>•</span>
                      <span>{item.timestamp}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1.5 shrink-0 justify-center">
                    <span className={`px-2 py-0.5 text-[8.5px] font-mono rounded border uppercase font-bold tracking-wider ${
                      item.status === "GRANTED" 
                        ? "bg-emerald-950/20 text-emerald-400 border-emerald-950 shadow-[0_0_10px_rgba(16,185,129,0.05)]" 
                        : item.status === "BLOCKED" 
                          ? "bg-rose-950/20 text-rose-400 border-rose-950" 
                          : "bg-amber-950/20 text-amber-500 border-amber-950"
                    }`}>
                      {item.status}
                    </span>
                    <span className="text-[8.5px] text-zinc-500 font-mono">
                      Conf: <span className="text-zinc-300 font-bold">{Math.round(item.confidence * 100)}%</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      <div className="text-center py-1 border-t border-zinc-900/40">
        <span className="text-[8px] font-mono text-zinc-650 uppercase">
          SECURE ENCRYPTED LEDGER LINK ENABLED • CloudSync-02 ENFORCEMENT SYNC • CONFIRM_SYNC OK
        </span>
      </div>

    </div>
  );
};
